import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  ExpandMore,
  Star,
  WorkOutline,
  TrendingUp,
  Assignment,
} from '@mui/icons-material';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const LeetcodeRoadmapPage: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  useEffect(() => {
    document.title = 'Complete LeetCode Roadmap | Ray Jones';
  }, []);

  const handleFaqChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const trackOutbound = (url: string, label: string) => {
    if (typeof window.gtag === "function") {
      // GA4-style event with descriptive name + parameters
      window.gtag("event", "outbound_click", {
        link_url: url,
        link_text: label,
        transport_type: "beacon",
      });
    }

    // Open the link in a new tab
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const painPoints = [
    "You can't even write a basic for loop without googling the syntax",
    "You're borderline failing your programming class with no idea how to improve", 
    "Every coding problem feels impossibly difficult and you don't know where to start",
    "You apply to hundreds of tech jobs but can't pass the coding interviews",
    "You see other students getting internships while you struggle with basic algorithms",
    "You want to build projects but don't understand fundamental programming concepts",
  ];

  const benefits = [
    "You confidently write loops, conditionals, and work with arrays like second nature",
    "You're excelling in your programming classes and helping other students understand concepts",
    "You systematically approach any coding problem with a clear methodology and confidence",
    "You pass coding interviews at top companies and receive multiple job offers",
    "You're the student others come to for help, landing prestigious internships and opportunities",
    "You build impressive projects that showcase your technical skills and land you dream jobs",
  ];

  const includedItems = [
    {
      title: "Complete Beginner Roadmap (50+ Problems)",
      description: "Start from absolute zero - learn loops, conditionals, and arrays through carefully selected problems that build your foundation step by step",
      icon: <Assignment />,
    },
    {
      title: "Fortune 500 Interview Roadmap (200+ Problems)",
      description: "The exact problems I solved to land my Fortune 500 job - data structures, algorithms, and system design patterns companies actually test",
      icon: <WorkOutline />,
    },
    {
      title: "FANG Interview Roadmap (400+ Problems)",
      description: "Advanced problem sets used by Google, Amazon, Facebook, Netflix - the comprehensive preparation for Big Tech interviews",
      icon: <Star />,
    },
    {
      title: "Problem-Solving Framework",
      description: "My systematic approach to breaking down any coding problem - the thinking process that turns impossible challenges into manageable steps",
      icon: <CheckCircle />,
    },
    {
      title: "Your Own Personal Map",
      description: "Study on Your Own Time. Adjust the Hours Per Day and Months that you want to work through the problems",
      icon: <TrendingUp />,
    },
  ];

  const faqs = [
    {
      question: "I've never coded before - is this too advanced for me?",
      answer: "Absolutely not! I literally started from not being able to write a for loop and almost failing my programming class. The beginner roadmap starts with the most basic problems like 'check if a number is in a list' and builds up systematically. This is exactly what I wish I had when I was struggling.",
    },
    {
      question: "How is this different from just practicing random LeetCode problems?",
      answer: "Random practice is why most people fail. This roadmap is carefully sequenced - each problem builds on the last one. You learn fundamental patterns first, then apply them to harder problems. I spent years figuring out the optimal order so you don't have to waste time on problems that are too hard or teach the wrong concepts.",
    },
    {
      question: "What if I'm already somewhat experienced?",
      answer: "Perfect! Skip to the intermediate or advanced roadmaps. The Fortune 500 roadmap got me my current job, and the FANG roadmap covers everything needed for Big Tech. Plus, the mock interviews will expose gaps in your knowledge that you didn't know existed.",
    },
    {
      question: "How long do I get Access?",
      answer: "You keep access as long as I'm still making videos. plus any updates I make. The roadmaps are based on real interview experiences and I continuously refine them based on feedback from students who land jobs.",
    },
    {
      question: "Can I really go from beginner to landing a tech job?",
      answer: "That's exactly what I did. I went from almost failing programming class to TA, to Citibank internship, to Fortune 500 engineer. The roadmap is literally my journey productized. However, I can't guarantee results - success depends on your effort and consistency with the practice.",
    },
    {
      question: "Why only $11? What's the catch?",
      answer: "No catch. I make my main income from 1-on-1 coaching at higher rates. I want to help as many people as possible break into tech, and $11 makes it accessible while filtering for people who are serious about improving.",
    },
    {
      question: "Can I get this for free in the community?",
      answer: "Yes! Join our $7/month community and get access to this roadmap plus ongoing support, code reviews, career guidance, and networking with other developers. The community is designed for ongoing growth, not just one-time materials.",
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      
      {/* Loom Video Section */}
      <Box sx={{ pt: 12, pb: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4, fontWeight: 'bold' }}
            >
              See How The <span style={{ color: '#88C0D0' }}>Roadmap</span> Works
            </Typography>
            <Paper sx={{ 
              p: 2, 
              bgcolor: 'background.paper', 
              borderRadius: 4,
              maxWidth: 800,
              mx: 'auto',
            }}>
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
              >
                <iframe
                  src="https://www.loom.com/embed/47225ebba8a243b6a2aa374a7806bed9?sid=0d9d0ca4-bc62-44db-a63c-587170905510?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true"
                  title="LeetCode Roadmap Video"
                  frameBorder="0"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Audience Callout */}
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', mb: 3, fontWeight: 'medium' }}
            >
              For Developers Who Are Tired of Failing Coding Interviews
            </Typography>
            
            {/* Bold Promise */}
            <Typography
              variant="h2"
              gutterBottom
              sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}
            >
              <span style={{ color: '#88C0D0' }}>Master Coding Interviews</span> Systematically
            </Typography>
            
            {/* Summary */}
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', mb: 6, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}
            >
              Get instant access to my complete beginner-to-FANG coding roadmap with 650+ carefully sequenced problems, systematic problem-solving framework that took me from failing programming class to Fortune 500 engineer, and the exact study plan I used to land internships at Citibank and my current tech job.
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=leetcode-roadmap'}
                sx={{
                  color: 'black',
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get Instant Access - $11
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => trackOutbound("https://www.skool.com/raymond/about", "Skool Community - Hero CTA")}
                sx={{
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(136, 192, 208, 0.1)',
                  },
                }}
              >
                Get It FREE in $7/month Community
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pain Points Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            If Any of This <span style={{ color: '#88C0D0' }}>Sounds Like You...</span>
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 8 }}>
            {painPoints.map((pain, index) => (
              <Box key={index}>
                <Card sx={{ 
                  bgcolor: 'rgba(255, 0, 0, 0.05)', 
                  border: '1px solid rgba(255, 0, 0, 0.2)',
                  height: '100%',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                      😣 {pain}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{ color: 'text.secondary', mb: 4, fontStyle: 'italic' }}
            >
              I know exactly how you feel. I was there too - struggling with basic programming concepts and watching other students succeed while I fell behind.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            Imagine If <span style={{ color: '#88C0D0' }}>Instead...</span>
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 8 }}>
            {benefits.map((benefit, index) => (
              <Box key={index}>
                <Card sx={{ 
                  bgcolor: 'rgba(0, 255, 0, 0.05)', 
                  border: '1px solid rgba(0, 255, 0, 0.2)',
                  height: '100%',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                      ✅ {benefit}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{ color: 'secondary.main', mb: 4, fontWeight: 'bold' }}
            >
              This transformation is 100% possible when you follow the right roadmap and practice systematically instead of randomly.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Solution Introduction */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 4 }}
          >
            Introducing: <span style={{ color: '#88C0D0' }}>The Complete LeetCode Roadmap</span>
          </Typography>
          
          <Typography
            variant="h5"
            align="center"
            sx={{ color: 'text.secondary', mb: 6, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}
          >
            This is the systematic, step-by-step coding journey that transformed me from a failing student into a Fortune 500 software engineer - exactly what you've been looking for.
          </Typography>
        </Container>
      </Box>

      {/* Discovery Story */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            How I <span style={{ color: '#88C0D0' }}>Discovered</span> This System
          </Typography>
          
          <Paper sx={{ p: 6, bgcolor: 'background.paper', borderRadius: 4 }}>
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              Four years ago, I was that struggling computer science student who couldn't write a for loop without Googling. I was borderline failing my programming class, watching other students excel while I couldn't even understand the basics. I felt completely lost and honestly considered switching majors.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              Then I discovered something that changed everything: instead of jumping into random hard problems, I needed to start with the absolute fundamentals. I began with the simplest possible problems - like checking if a number exists in a list. These tiny wins built my confidence and taught me core concepts.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              I spent the next two years methodically working through problems, documenting which ones taught me the most, and discovering the optimal sequence that builds programming intuition. I went from struggling student to TA for data structures and algorithms, then landed an internship at Citibank.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              But I didn't stop there. I continued refining my approach through real interviews, tracking which problems appeared most frequently, and developing frameworks for approaching any coding challenge. This eventually got me my current role as a software engineer at a Fortune 500 company.
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'secondary.main', fontSize: '1.2rem', lineHeight: 1.8, fontWeight: 'bold' }}>
              Nobody else could have created this exact roadmap because nobody else lived through this specific journey of going from complete beginner to industry professional. This isn't theory - it's a battle-tested path that actually works because I walked it myself and documented every step.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* What's Included Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            What's <span style={{ color: '#88C0D0' }}>Included</span>
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4, mb: 8 }}>
            {includedItems.map((item, index) => (
              <Box key={index}>
                <Card sx={{ 
                  bgcolor: 'background.default', 
                  height: '100%',
                  border: '2px solid #333',
                  '&:hover': {
                    border: '2px solid #88C0D0',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ mr: 2, color: 'secondary.main' }}>
                        {item.icon}
                      </Box>
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Paper sx={{ 
              p: 4, 
              bgcolor: 'rgba(136, 192, 208, 0.1)', 
              border: '2px solid #88C0D0',
              borderRadius: 4,
              maxWidth: 600,
              mx: 'auto',
              mb: 6,
            }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                650+ problems, systematic frameworks, and 1-on-1 coaching - all for less than a single coding bootcamp lecture
              </Typography>
            </Paper>

            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=leetcode-roadmap'}
                sx={{
                  color: 'black',
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get Instant Access - $11
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => trackOutbound("https://www.skool.com/raymond/about", "Skool Community - Included Section CTA")}
                sx={{
                  px: 6,
                  py: 2,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(136, 192, 208, 0.1)',
                  },
                }}
              >
                Get It FREE in $7/month Community
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ color: 'text.primary', mb: 6 }}
          >
            Frequently Asked <span style={{ color: '#88C0D0' }}>Questions</span>
          </Typography>
          
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion 
                key={index}
                expanded={expandedFaq === `panel${index}`}
                onChange={handleFaqChange(`panel${index}`)}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid #333',
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    border: '1px solid #88C0D0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: 'secondary.main' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      margin: '16px 0',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 4, 
                bgcolor: 'rgba(136, 192, 208, 0.1)',
                border: '2px solid #88C0D0',
                color: 'white',
                '& .MuiAlert-icon': {
                  color: '#88C0D0',
                },
                fontSize: '1.1rem',
                fontWeight: 'bold',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              ⚠️ Don't spend another month struggling with random problems. Every day you wait is another day your dream job goes to someone who practiced systematically.
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=leetcode-roadmap'}
                sx={{
                  color: 'black',
                  px: 8,
                  py: 3,
                  fontWeight: 'bold',
                  fontSize: '1.4rem',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get Your Complete Roadmap Now - $11
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => trackOutbound("https://www.skool.com/raymond/about", "Skool Community - Final CTA")}
                sx={{
                  px: 8,
                  py: 3,
                  fontWeight: 'bold',
                  fontSize: '1.4rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(136, 192, 208, 0.1)',
                  },
                }}
              >
                Join $7/month Community Instead
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default LeetcodeRoadmapPage;